import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TextField,
  Button,
  TableSortLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import axiosInstance from './axiosInstance';
import { formatDate } from './dateUtils';

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '£0.00';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const BoldCellText = ({ children, align = 'inherit' }) => (
  <Typography component="strong" sx={{ display: 'block', fontWeight: 900, textAlign: align }}>
    {children}
  </Typography>
);

const formatSupplierLabel = (record) => {
  const name = (record?.supplier_name ?? '').toString().trim();
  const id = (record?.supplier_id ?? '').toString().trim();
  if (name && id && name !== id) return `${name} (${id})`;
  return name || id;
};

const EBAY_SUPPLIER_ID = '3500000606';
const COMPANY_CREDIT_CARD_SUPPLIER_ID = '3500000518';
const COMBINED_SUPPLIER_ID = `${EBAY_SUPPLIER_ID} / ${COMPANY_CREDIT_CARD_SUPPLIER_ID}`;
const COMBINED_SUPPLIER_LABEL = 'Ebay/Company Credit Card';
const EMPTY_DETAIL_SUMMARY = { total_net: 0, record_count: 0 };

const SapBreakdownTab = ({ baseFilters, panelWidth }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summarySort, setSummarySort] = useState({ key: 'vault_overall_net', direction: 'desc' });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailRecords, setDetailRecords] = useState({ sap: [], vault: [] });
  const [detailSummary, setDetailSummary] = useState({
    sap: EMPTY_DETAIL_SUMMARY,
    vault: EMPTY_DETAIL_SUMMARY,
  });
  const [detailTitle, setDetailTitle] = useState('');
  const [detailSupplierId, setDetailSupplierId] = useState('');
  const [detailAllSuppliers, setDetailAllSuppliers] = useState(false);
  const [detailCombinedSuppliers, setDetailCombinedSuppliers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMismatchesOnly, setShowMismatchesOnly] = useState(false);
  const [vaultSort, setVaultSort] = useState({ key: 'invoice_date', direction: 'desc' });
  const [sapSort, setSapSort] = useState({ key: 'document_date', direction: 'desc' });
  const [sapMissingFilter, setSapMissingFilter] = useState('all');
  const [vaultMissingFilter, setVaultMissingFilter] = useState('all');
  const [detailHasLoadedOnce, setDetailHasLoadedOnce] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [mergeEbayCompanyCard, setMergeEbayCompanyCard] = useState(true);
  const detailPanelRef = useRef(null);

  const supplierNameMap = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (row?.supplier_id) {
        map.set(row.supplier_id, row.supplier_name || row.supplier_id);
      }
    });
    return map;
  }, [rows]);

  const docTypeLabelMap = useMemo(() => {
    const map = new Map();
    (docTypes || []).forEach((type) => {
      if (type?.doc_type_id) {
        map.set(type.doc_type_id.toString().toLowerCase(), type.name || type.doc_type_id);
      }
    });
    return map;
  }, [docTypes]);

  const calculateAccuracy = (vaultOverall, sapOverall) => {
    const vaultValue = Number(vaultOverall || 0);
    const sapValue = Number(sapOverall || 0);
    const diff = Math.abs(vaultValue - sapValue);
    const base = Math.max(Math.abs(vaultValue), Math.abs(sapValue), 1);
    return Math.max(0, 100 - (diff / base) * 100);
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        sap_invoice_net: acc.sap_invoice_net + Number(row.sap_invoice_net || 0),
        sap_credit_net: acc.sap_credit_net + Number(row.sap_credit_net || 0),
        sap_overall_net: acc.sap_overall_net + Number(row.sap_overall_net || 0),
        vault_invoice_net: acc.vault_invoice_net + Number(row.vault_invoice_net || 0),
        vault_other_net: acc.vault_other_net + Number(row.vault_other_net || 0),
        vault_credit_net: acc.vault_credit_net + Number(row.vault_credit_net || 0),
        vault_overall_net: acc.vault_overall_net + Number(row.vault_overall_net || 0),
      }),
      {
        sap_invoice_net: 0,
        sap_credit_net: 0,
        sap_overall_net: 0,
        vault_invoice_net: 0,
        vault_other_net: 0,
        vault_credit_net: 0,
        vault_overall_net: 0,
      }
    );
  }, [rows]);

  const compareValues = (aValue, bValue, direction, type = 'string') => {
    let a = aValue;
    let b = bValue;
    if (type === 'number') {
      a = Number(aValue || 0);
      b = Number(bValue || 0);
    } else if (type === 'date') {
      a = aValue ? new Date(aValue).getTime() : 0;
      b = bValue ? new Date(bValue).getTime() : 0;
    } else {
      a = (aValue ?? '').toString().toLowerCase();
      b = (bValue ?? '').toString().toLowerCase();
    }
    if (a < b) return direction === 'asc' ? -1 : 1;
    if (a > b) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedRows = useMemo(() => {
    if (!summarySort?.key) {
      return [...rows].sort(
        (a, b) => Number(b.vault_overall_net || 0) - Number(a.vault_overall_net || 0)
      );
    }
    const key = summarySort.key;
    const type = [
      'vault_invoice_net',
      'vault_other_net',
      'sap_invoice_net',
      'vault_credit_net',
      'sap_credit_net',
      'vault_overall_net',
      'sap_overall_net',
      'accuracy_percent',
    ].includes(key)
      ? 'number'
      : 'string';
    return [...rows].sort((a, b) => compareValues(a[key], b[key], summarySort.direction, type));
  }, [rows, summarySort]);

  const displayRows = useMemo(() => {
    if (!mergeEbayCompanyCard) return sortedRows;
    const ebayRow = rows.find((row) => row?.supplier_id === EBAY_SUPPLIER_ID);
    const companyCardRow = rows.find((row) => row?.supplier_id === COMPANY_CREDIT_CARD_SUPPLIER_ID);
    if (!ebayRow && !companyCardRow) return sortedRows;
    const baseRows = rows.filter(
      (row) => row?.supplier_id !== EBAY_SUPPLIER_ID && row?.supplier_id !== COMPANY_CREDIT_CARD_SUPPLIER_ID
    );
    const combined = {
      supplier_id: COMBINED_SUPPLIER_ID,
      supplier_name: COMBINED_SUPPLIER_LABEL,
      is_unspecified_supplier: false,
      vault_invoice_net:
        Number(ebayRow?.vault_invoice_net || 0) + Number(companyCardRow?.vault_invoice_net || 0),
      vault_other_net:
        Number(ebayRow?.vault_other_net || 0) + Number(companyCardRow?.vault_other_net || 0),
      sap_invoice_net:
        Number(ebayRow?.sap_invoice_net || 0) + Number(companyCardRow?.sap_invoice_net || 0),
      vault_credit_net:
        Number(ebayRow?.vault_credit_net || 0) + Number(companyCardRow?.vault_credit_net || 0),
      sap_credit_net:
        Number(ebayRow?.sap_credit_net || 0) + Number(companyCardRow?.sap_credit_net || 0),
      vault_overall_net:
        Number(ebayRow?.vault_overall_net || 0) + Number(companyCardRow?.vault_overall_net || 0),
      sap_overall_net:
        Number(ebayRow?.sap_overall_net || 0) + Number(companyCardRow?.sap_overall_net || 0),
    };
    combined.accuracy_percent = calculateAccuracy(combined.vault_overall_net, combined.sap_overall_net);
    const mergedRows = [...baseRows, combined];
    if (!summarySort?.key) {
      return mergedRows.sort(
        (a, b) => Number(b.vault_overall_net || 0) - Number(a.vault_overall_net || 0)
      );
    }
    const key = summarySort.key;
    const type = [
      'vault_invoice_net',
      'vault_other_net',
      'sap_invoice_net',
      'vault_credit_net',
      'sap_credit_net',
      'vault_overall_net',
      'sap_overall_net',
      'accuracy_percent',
    ].includes(key)
      ? 'number'
      : 'string';
    return mergedRows.sort((a, b) => compareValues(a[key], b[key], summarySort.direction, type));
  }, [mergeEbayCompanyCard, rows, sortedRows, summarySort, compareValues, calculateAccuracy]);

  const normalizeDocType = (value) => (value ?? '').toString().trim().toLowerCase();
  const normalizeDocNumber = (value) =>
    (value ?? '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const buildMatchKey = (docType, docNumber, supplierId) => {
    const type = normalizeDocType(docType);
    const number = normalizeDocNumber(docNumber);
    if (!type || !number) return '';
    if (!detailAllSuppliers && !detailCombinedSuppliers) return `${type}::${number}`;
    const supplier = (supplierId ?? '').toString().trim().toLowerCase();
    if (!supplier) return '';
    return `${supplier}::${type}::${number}`;
  };

  const getDocTypeLabel = (value) => {
    if (!value) return '';
    const key = value.toString().toLowerCase();
    return docTypeLabelMap.get(key) || value;
  };

  const getDetailPayload = (response) => response?.data?.data?.data || response?.data?.data || {};

  const normalizeDetailSummary = (summary) => ({
    ...EMPTY_DETAIL_SUMMARY,
    ...(summary && typeof summary === 'object' ? summary : {}),
  });

  const vaultNetsByKey = useMemo(() => {
    const map = new Map();
    (detailRecords.vault || []).forEach((record) => {
      const key = buildMatchKey(record.doc_type_id, record.invoice_number, record.supplier_id);
      if (!key) return;
      const list = map.get(key) || [];
      list.push(Number(record.net || 0));
      map.set(key, list);
    });
    return map;
  }, [detailRecords.vault]);

  const sapNetsByKey = useMemo(() => {
    const map = new Map();
    (detailRecords.sap || []).forEach((record) => {
      const key = buildMatchKey(record.document_type, record.document_number, record.supplier_id);
      if (!key) return;
      const list = map.get(key) || [];
      list.push(Number(record.net || 0));
      map.set(key, list);
    });
    return map;
  }, [detailRecords.sap]);


  const isMissingFlag = (value) => {
    if (value === true) return true;
    if (value === false || value === null || value === undefined) return false;
    const text = String(value).trim().toLowerCase();
    if (text === 'yes' || text === 'true' || text === '1') return true;
    if (text === 'no' || text === 'false' || text === '0') return false;
    return Boolean(value);
  };

  const mismatchPairs = useMemo(() => {
    const vaultByKey = new Map();
    const sapByKey = new Map();
    (detailRecords.vault || []).forEach((record) => {
      const key = buildMatchKey(record.doc_type_id, record.invoice_number, record.supplier_id);
      if (key) vaultByKey.set(key, record);
    });
    (detailRecords.sap || []).forEach((record) => {
      const key = buildMatchKey(record.document_type, record.document_number, record.supplier_id);
      if (key) sapByKey.set(key, record);
    });
    const pairs = [];
    vaultByKey.forEach((vaultRecord, key) => {
      const sapRecord = sapByKey.get(key);
      if (!sapRecord) return;
      const vaultMissing = isMissingFlag(vaultRecord.missing_in_sap);
      const sapMissing = isMissingFlag(sapRecord.missing_in_vault);
      if (vaultMissing || sapMissing) return;
      if (Number(vaultRecord.net || 0) === Number(sapRecord.net || 0)) return;
      pairs.push({ key, vault: vaultRecord, sap: sapRecord });
    });
    return pairs;
  }, [detailRecords]);

  const vaultDetailRows = useMemo(() => {
    const baseRecords = showMismatchesOnly
      ? mismatchPairs.map((pair) => pair.vault)
      : (detailRecords.vault || []);
    if (showMismatchesOnly) return baseRecords;
    return baseRecords
      .filter((record) => {
        if (!vaultMissingFilter || vaultMissingFilter === 'all') return true;
        const missing = Boolean(record.missing_in_sap);
        return vaultMissingFilter === 'yes' ? missing : !missing;
      })
      .sort((a, b) => {
        const key = vaultSort.key;
        if (key === 'supplier') {
          const aValue = formatSupplierLabel(a);
          const bValue = formatSupplierLabel(b);
          return compareValues(aValue, bValue, vaultSort.direction, 'string');
        }
        const type = key === 'net' ? 'number' : key === 'invoice_date' ? 'date' : 'string';
        return compareValues(a[key], b[key], vaultSort.direction, type);
      });
  }, [detailRecords.vault, mismatchPairs, showMismatchesOnly, vaultMissingFilter, vaultSort]);

  const sapDetailRows = useMemo(() => {
    const baseRecords = showMismatchesOnly
      ? mismatchPairs.map((pair) => pair.sap)
      : (detailRecords.sap || []);
    if (showMismatchesOnly) return baseRecords;
    return baseRecords
      .filter((record) => {
        if (!sapMissingFilter || sapMissingFilter === 'all') return true;
        const missing = Boolean(record.missing_in_vault);
        return sapMissingFilter === 'yes' ? missing : !missing;
      })
      .sort((a, b) => {
        const key = sapSort.key;
        if (key === 'supplier') {
          const aValue = formatSupplierLabel(a);
          const bValue = formatSupplierLabel(b);
          return compareValues(aValue, bValue, sapSort.direction, 'string');
        }
        const type = key === 'net' ? 'number' : key === 'document_date' ? 'date' : 'string';
        return compareValues(a[key], b[key], sapSort.direction, type);
      });
  }, [detailRecords.sap, mismatchPairs, showMismatchesOnly, sapMissingFilter, sapSort]);

  const vaultDetailTotalNet = useMemo(
    () => vaultDetailRows.reduce((sum, record) => sum + Number(record.net || 0), 0),
    [vaultDetailRows]
  );

  const sapDetailTotalNet = useMemo(
    () => sapDetailRows.reduce((sum, record) => sum + Number(record.net || 0), 0),
    [sapDetailRows]
  );

  const vaultOverallTotalNet = Number(detailSummary.vault?.total_net || 0);
  const sapOverallTotalNet = Number(detailSummary.sap?.total_net || 0);
  const vaultFilterActive = Boolean(showMismatchesOnly || (vaultMissingFilter && vaultMissingFilter !== 'all'));
  const sapFilterActive = Boolean(showMismatchesOnly || (sapMissingFilter && sapMissingFilter !== 'all'));

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return displayRows;
    return displayRows.filter((row) => {
      const name = (row.supplier_name || '').toString().toLowerCase();
      const id = (row.supplier_id || '').toString().toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  }, [displayRows, searchTerm]);

  const filteredTotals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => ({
        sap_invoice_net: acc.sap_invoice_net + Number(row.sap_invoice_net || 0),
        sap_credit_net: acc.sap_credit_net + Number(row.sap_credit_net || 0),
        sap_overall_net: acc.sap_overall_net + Number(row.sap_overall_net || 0),
        vault_invoice_net: acc.vault_invoice_net + Number(row.vault_invoice_net || 0),
        vault_other_net: acc.vault_other_net + Number(row.vault_other_net || 0),
        vault_credit_net: acc.vault_credit_net + Number(row.vault_credit_net || 0),
        vault_overall_net: acc.vault_overall_net + Number(row.vault_overall_net || 0),
      }),
      {
        sap_invoice_net: 0,
        sap_credit_net: 0,
        sap_overall_net: 0,
        vault_invoice_net: 0,
        vault_other_net: 0,
        vault_credit_net: 0,
        vault_overall_net: 0,
      }
    );
  }, [filteredRows]);

  const params = useMemo(() => {
    const nextParams = {
      start_date: baseFilters?.startDate,
      end_date: baseFilters?.endDate,
    };
    if (baseFilters?.locationId) {
      nextParams.location_id = baseFilters.locationId;
    }
    if (baseFilters?.supplierId) {
      nextParams.supplier_id = baseFilters.supplierId;
    }
    if (baseFilters?.nominalCodeId) {
      nextParams.nominal_code_id = baseFilters.nominalCodeId;
    }
    return nextParams;
  }, [baseFilters]);

  useEffect(() => {
    let isMounted = true;
    const fetchDocTypes = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/doc/type');
        if (!isMounted) return;
        setDocTypes(response?.data?.data || []);
      } catch (err) {
        if (!isMounted) return;
        setDocTypes([]);
      }
    };
    fetchDocTypes();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!detailOpen && !detailSupplierId) return;
    setDetailOpen(false);
    setDetailRecords({ sap: [], vault: [] });
    setDetailError('');
    setDetailSupplierId('');
    setDetailAllSuppliers(false);
    setDetailCombinedSuppliers(false);
    setSapMissingFilter('all');
    setVaultMissingFilter('all');
    setDetailHasLoadedOnce(false);
  }, [
    baseFilters?.locationId,
    baseFilters?.supplierId,
    baseFilters?.startDate,
    baseFilters?.endDate,
    baseFilters?.costBy,
    baseFilters?.nominalCodeId,
  ]);

  useEffect(() => {
    if (!detailOpen && !detailSupplierId) return;
    setDetailOpen(false);
    setDetailRecords({ sap: [], vault: [] });
    setDetailError('');
    setDetailSupplierId('');
    setDetailAllSuppliers(false);
    setDetailCombinedSuppliers(false);
    setSapMissingFilter('all');
    setVaultMissingFilter('all');
    setDetailHasLoadedOnce(false);
  }, [searchTerm]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setError('');
      try {
        const response = await axiosInstance.get('/api/v1/sap/breakdown', { params });
        const data = response?.data?.data?.data || response?.data?.data || [];
        if (!isMounted) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        setError('Unable to load SAP breakdown data.');
        setRows([]);
      } finally {
        if (isMounted) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };

    if (params.start_date && params.end_date) {
      setLoading(true);
      fetchData();
    }
    return () => {
      isMounted = false;
    };
  }, [params.start_date, params.end_date, params.location_id, params.supplier_id, params.nominal_code_id]);

  const missingHighlightColor = '#fdeaea';

  const openDetails = async (supplierId, supplierName) => {
    if (mergeEbayCompanyCard && supplierId === COMBINED_SUPPLIER_ID) {
      openCombinedDetails();
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailRecords({ sap: [], vault: [] });
    setDetailSummary({ sap: EMPTY_DETAIL_SUMMARY, vault: EMPTY_DETAIL_SUMMARY });
    setDetailSupplierId(supplierId);
    setDetailAllSuppliers(false);
    setDetailCombinedSuppliers(false);
    setSapMissingFilter('all');
    setVaultMissingFilter('all');
    setShowMismatchesOnly(false);
    setDetailTitle(`${supplierName || supplierId} - SAP vs Vault Records`);
    try {
      const [sapResponse, vaultResponse] = await Promise.all([
        axiosInstance.get('/api/v1/sap/breakdown/details', {
          params: {
            supplier_id: supplierId,
            source: 'sap',
            start_date: params.start_date,
            end_date: params.end_date,
            location_id: params.location_id,
            nominal_code_id: params.nominal_code_id,
          },
        }),
        axiosInstance.get('/api/v1/sap/breakdown/details', {
          params: {
            supplier_id: supplierId,
            source: 'vault',
            start_date: params.start_date,
            end_date: params.end_date,
            location_id: params.location_id,
            nominal_code_id: params.nominal_code_id,
          },
        }),
      ]);
      const sapPayload = getDetailPayload(sapResponse);
      const vaultPayload = getDetailPayload(vaultResponse);
      const sapData = sapPayload?.records || [];
      const vaultData = vaultPayload?.records || [];
      setDetailRecords({
        sap: Array.isArray(sapData)
          ? sapData.map((record) => ({
              ...record,
              supplier_id: record.supplier_id || supplierId,
              supplier_name: supplierNameMap.get(record.supplier_id || supplierId) || supplierName || supplierId,
            }))
          : [],
        vault: Array.isArray(vaultData)
          ? vaultData.map((record) => ({
              ...record,
              supplier_id: record.supplier_id || supplierId,
              supplier_name: supplierNameMap.get(record.supplier_id || supplierId) || supplierName || supplierId,
            }))
          : [],
      });
      setDetailSummary({
        sap: normalizeDetailSummary(sapPayload?.summary),
        vault: normalizeDetailSummary(vaultPayload?.summary),
      });
    } catch (err) {
      setDetailError('Unable to load breakdown details.');
    } finally {
      setDetailLoading(false);
      setDetailHasLoadedOnce(true);
    }
    requestAnimationFrame(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const openCombinedDetails = async () => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailRecords({ sap: [], vault: [] });
    setDetailSummary({ sap: EMPTY_DETAIL_SUMMARY, vault: EMPTY_DETAIL_SUMMARY });
    setDetailSupplierId(COMBINED_SUPPLIER_ID);
    setDetailAllSuppliers(false);
    setDetailCombinedSuppliers(true);
    setSapMissingFilter('all');
    setVaultMissingFilter('all');
    setShowMismatchesOnly(false);
    setDetailTitle(`${COMBINED_SUPPLIER_LABEL} - SAP vs Vault Records`);
    try {
      const supplierIds = [EBAY_SUPPLIER_ID, COMPANY_CREDIT_CARD_SUPPLIER_ID];
      const requests = supplierIds.flatMap((id) => [
        axiosInstance.get('/api/v1/sap/breakdown/details', {
          params: {
            supplier_id: id,
            source: 'sap',
            start_date: params.start_date,
            end_date: params.end_date,
            location_id: params.location_id,
            nominal_code_id: params.nominal_code_id,
          },
        }),
        axiosInstance.get('/api/v1/sap/breakdown/details', {
          params: {
            supplier_id: id,
            source: 'vault',
            start_date: params.start_date,
            end_date: params.end_date,
            location_id: params.location_id,
            nominal_code_id: params.nominal_code_id,
          },
        }),
      ]);
      const responses = await Promise.all(requests);
      const sapData = [];
      const vaultData = [];
      let sapTotalNet = 0;
      let vaultTotalNet = 0;
      let sapRecordCount = 0;
      let vaultRecordCount = 0;
      for (let i = 0; i < responses.length; i += 2) {
        const sapPayload = getDetailPayload(responses[i]);
        const vaultPayload = getDetailPayload(responses[i + 1]);
        const sapRows = sapPayload?.records || [];
        const vaultRows = vaultPayload?.records || [];
        sapData.push(...(Array.isArray(sapRows) ? sapRows : []));
        vaultData.push(...(Array.isArray(vaultRows) ? vaultRows : []));
        const sapSummary = normalizeDetailSummary(sapPayload?.summary);
        const vaultSummary = normalizeDetailSummary(vaultPayload?.summary);
        sapTotalNet += Number(sapSummary.total_net || 0);
        vaultTotalNet += Number(vaultSummary.total_net || 0);
        sapRecordCount += Number(sapSummary.record_count || 0);
        vaultRecordCount += Number(vaultSummary.record_count || 0);
      }
      setDetailRecords({
        sap: sapData.map((record) => ({
          ...record,
          supplier_name: supplierNameMap.get(record.supplier_id) || record.supplier_id || '',
        })),
        vault: vaultData.map((record) => ({
          ...record,
          supplier_name: supplierNameMap.get(record.supplier_id) || record.supplier_id || '',
        })),
      });
      setDetailSummary({
        sap: { total_net: sapTotalNet, record_count: sapRecordCount },
        vault: { total_net: vaultTotalNet, record_count: vaultRecordCount },
      });
    } catch (err) {
      setDetailError('Unable to load breakdown details.');
    } finally {
      setDetailLoading(false);
      setDetailHasLoadedOnce(true);
    }
    requestAnimationFrame(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const openAllDetails = async () => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailRecords({ sap: [], vault: [] });
    setDetailSummary({ sap: EMPTY_DETAIL_SUMMARY, vault: EMPTY_DETAIL_SUMMARY });
    setDetailSupplierId('');
    setDetailAllSuppliers(true);
    setDetailCombinedSuppliers(false);
    setSapMissingFilter('');
    setVaultMissingFilter('');
    setShowMismatchesOnly(false);
    setDetailTitle('All Suppliers - SAP vs Vault Records');
    try {
      const [sapResponse, vaultResponse] = await Promise.all([
        axiosInstance.get('/api/v1/sap/breakdown/details', {
          params: {
            source: 'sap',
            start_date: params.start_date,
            end_date: params.end_date,
            location_id: params.location_id,
            nominal_code_id: params.nominal_code_id,
          },
        }),
        axiosInstance.get('/api/v1/sap/breakdown/details', {
          params: {
            source: 'vault',
            start_date: params.start_date,
            end_date: params.end_date,
            location_id: params.location_id,
            nominal_code_id: params.nominal_code_id,
          },
        }),
      ]);
      const sapPayload = getDetailPayload(sapResponse);
      const vaultPayload = getDetailPayload(vaultResponse);
      const sapData = sapPayload?.records || [];
      const vaultData = vaultPayload?.records || [];
      setDetailRecords({
        sap: Array.isArray(sapData)
          ? sapData.map((record) => ({
              ...record,
              supplier_name: supplierNameMap.get(record.supplier_id) || record.supplier_id || '',
            }))
          : [],
        vault: Array.isArray(vaultData)
          ? vaultData.map((record) => ({
              ...record,
              supplier_name: supplierNameMap.get(record.supplier_id) || record.supplier_id || '',
            }))
          : [],
      });
      setDetailSummary({
        sap: normalizeDetailSummary(sapPayload?.summary),
        vault: normalizeDetailSummary(vaultPayload?.summary),
      });
    } catch (err) {
      setDetailError('Unable to load breakdown details.');
    } finally {
      setDetailLoading(false);
      setDetailHasLoadedOnce(true);
    }
    requestAnimationFrame(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const closeDetails = () => {
    setDetailOpen(false);
    setDetailRecords({ sap: [], vault: [] });
    setDetailSummary({ sap: EMPTY_DETAIL_SUMMARY, vault: EMPTY_DETAIL_SUMMARY });
    setDetailError('');
    setDetailSupplierId('');
    setDetailAllSuppliers(false);
    setDetailCombinedSuppliers(false);
    setSapMissingFilter('');
    setVaultMissingFilter('');
    setDetailHasLoadedOnce(false);
    setShowMismatchesOnly(false);
  };

  const panelPaperSx = panelWidth
    ? { width: panelWidth, maxWidth: panelWidth, ml: 'auto', mr: 0, boxSizing: 'border-box' }
    : { width: '100%' };

  const isUnspecifiedSupplier = (row) => row?.is_unspecified_supplier || row?.supplier_id === 'Unspecified Supplier';

  const formatNumber = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return number.toFixed(2);
  };

  const escapeCsvValue = (value) => {
    const text = String(value ?? '');
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const downloadCsv = (filename, columns, rows) => {
    if (!rows.length) return;
    const csvLines = [
      columns.map((column) => escapeCsvValue(column.label)).join(','),
      ...rows.map((row) => columns.map((column) => escapeCsvValue(row[column.key])).join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCsvTextValue = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    if (!text) return '';
    return `="${text.replace(/"/g, '""')}"`;
  };

  const handleDownloadCsv = () => {
    const rowsToExport = filteredRows;
    if (!rowsToExport.length) return;
    const columns = [
      { key: '#', label: '#' },
      { key: 'supplier_name', label: 'Supplier Name' },
      { key: 'supplier_id', label: 'Supplier Id' },
      { key: 'vault_other_net', label: 'Vault Other Amount (Net)' },
      { key: 'vault_invoice_net', label: 'Vault Invoice Amount (Net)' },
      { key: 'sap_invoice_net', label: 'SAP Invoice Amount (Net)' },
      { key: 'vault_credit_net', label: 'Vault Credit Amount (Net)' },
      { key: 'sap_credit_net', label: 'SAP Credit Amount (Net)' },
      { key: 'vault_overall_net', label: 'Vault Overall Amount (Net)' },
      { key: 'sap_overall_net', label: 'SAP Overall Amount (Net)' },
      { key: 'accuracy_percent', label: 'Accuracy (%)' },
    ];
    const rows = [
      ...rowsToExport.map((row, index) => {
        const accuracy = Number(
          row.accuracy_percent || calculateAccuracy(row.vault_overall_net, row.sap_overall_net)
        ).toFixed(2);
        return {
          '#': index + 1,
          supplier_name: row.supplier_name ?? '',
          supplier_id: row.supplier_id ?? '',
          vault_invoice_net: formatNumber(row.vault_invoice_net),
          vault_other_net: formatNumber(row.vault_other_net),
          sap_invoice_net: formatNumber(row.sap_invoice_net),
          vault_credit_net: formatNumber(row.vault_credit_net),
          sap_credit_net: formatNumber(row.sap_credit_net),
          vault_overall_net: formatNumber(row.vault_overall_net),
          sap_overall_net: formatNumber(row.sap_overall_net),
          accuracy_percent: accuracy,
        };
      }),
      {
        '#': '',
        supplier_name: 'Total',
        supplier_id: '',
        vault_invoice_net: formatNumber(filteredTotals.vault_invoice_net),
        vault_other_net: formatNumber(filteredTotals.vault_other_net),
        sap_invoice_net: formatNumber(filteredTotals.sap_invoice_net),
        vault_credit_net: formatNumber(filteredTotals.vault_credit_net),
        sap_credit_net: formatNumber(filteredTotals.sap_credit_net),
        vault_overall_net: formatNumber(filteredTotals.vault_overall_net),
        sap_overall_net: formatNumber(filteredTotals.sap_overall_net),
        accuracy_percent: calculateAccuracy(filteredTotals.vault_overall_net, filteredTotals.sap_overall_net).toFixed(2),
      },
    ];
    downloadCsv('sap_breakdown.csv', columns, rows);
  };

  const handleDownloadVaultDetailsCsv = () => {
    if (!vaultDetailRows.length) return;
    const columns = [
      { key: 'document_number', label: 'Document Number' },
      { key: 'supplier_id', label: 'Supplier Id' },
      { key: 'supplier_name', label: 'Supplier Name' },
      { key: 'doc_type_id', label: 'Type' },
      { key: 'invoice_date', label: 'Date' },
      { key: 'net', label: 'Net' },
      { key: 'missing_in_sap', label: 'Missing In SAP' },
    ];
    const rows = [
      ...vaultDetailRows.map((record) => ({
        document_number: formatCsvTextValue(record.invoice_number),
        supplier_id: record.supplier_id ?? '',
        supplier_name: record.supplier_name ?? '',
        doc_type_id: getDocTypeLabel(record.doc_type_id ?? ''),
        invoice_date: formatDate(record.invoice_date) || '',
        net: formatNumber(record.net),
        missing_in_sap: isMissingFlag(record.missing_in_sap) ? 'Yes' : 'No',
      })),
      {
        document_number: vaultFilterActive ? 'Filtered Total' : 'Total',
        supplier_id: '',
        supplier_name: '',
        doc_type_id: '',
        invoice_date: '',
        net: formatNumber(vaultDetailTotalNet),
        missing_in_sap: '',
      },
    ];
    if (vaultFilterActive) {
      rows.push({
        document_number: 'Overall Total',
        supplier_id: '',
        supplier_name: '',
        doc_type_id: '',
        invoice_date: '',
        net: formatNumber(vaultOverallTotalNet),
        missing_in_sap: '',
      });
    }
    downloadCsv('sap_breakdown_vault_records.csv', columns, rows);
  };

  const handleDownloadSapDetailsCsv = () => {
    if (!sapDetailRows.length) return;
    const columns = [
      { key: 'document_number', label: 'Document Number' },
      { key: 'supplier_id', label: 'Supplier Id' },
      { key: 'supplier_name', label: 'Supplier Name' },
      { key: 'document_type', label: 'Type' },
      { key: 'document_date', label: 'Date' },
      { key: 'net', label: 'Net' },
      { key: 'missing_in_vault', label: 'Missing In Vault' },
    ];
    const rows = [
      ...sapDetailRows.map((record) => ({
        document_number: formatCsvTextValue(record.document_number),
        supplier_id: record.supplier_id ?? '',
        supplier_name: record.supplier_name ?? '',
        document_type: getDocTypeLabel(record.document_type ?? ''),
        document_date: formatDate(record.document_date) || '',
        net: formatNumber(record.net),
        missing_in_vault: isMissingFlag(record.missing_in_vault) ? 'Yes' : 'No',
      })),
      {
        document_number: sapFilterActive ? 'Filtered Total' : 'Total',
        supplier_id: '',
        supplier_name: '',
        document_type: '',
        document_date: '',
        net: formatNumber(sapDetailTotalNet),
        missing_in_vault: '',
      },
    ];
    if (sapFilterActive) {
      rows.push({
        document_number: 'Overall Total',
        supplier_id: '',
        supplier_name: '',
        document_type: '',
        document_date: '',
        net: formatNumber(sapOverallTotalNet),
        missing_in_vault: '',
      });
    }
    downloadCsv('sap_breakdown_sap_records.csv', columns, rows);
  };

  return (
    <Box sx={{ mt: 3, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {loading && !hasLoadedOnce ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, ...panelPaperSx }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 2, mb: 2, ...panelPaperSx }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 3 }, position: 'relative', ...panelPaperSx }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.65)',
                zIndex: 2,
              }}
            />
          )}
          <Box
            sx={{
              mb: 2,
              display: 'grid',
              alignItems: 'center',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                width: 'fit-content',
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Search Supplier"
                placeholder="Search by name or id"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                sx={{ flex: '0 0 300px', minWidth: 260, maxWidth: 320 }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                SAP Breakdown
              </Typography>
            </Box>
            <Box
              sx={{
                justifySelf: { sm: 'end' },
                display: 'flex',
                gap: 1,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                alignItems: 'center',
              }}
            >
              <Button
                variant={mergeEbayCompanyCard ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => setMergeEbayCompanyCard((prev) => !prev)}
                size="medium"
                sx={{ whiteSpace: 'nowrap', minHeight: 36 }}
              >
                Merge Ebay/Company Credit Card
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={openAllDetails}
                size="medium"
                sx={{ whiteSpace: 'nowrap', minHeight: 36, flexShrink: 0 }}
                disabled={loading || detailLoading}
              >
                View All Records
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownloadCsv}
                size="medium"
                sx={{ whiteSpace: 'nowrap', minHeight: 36, px: 4, flexShrink: 0 }}
              >
                Download CSV
              </Button>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: '55vh', overflowX: 'auto', overflowY: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={summarySort.key === 'supplier_name'}
                      direction={summarySort.key === 'supplier_name' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'supplier_name',
                          direction: prev.key === 'supplier_name' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Supplier Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={summarySort.key === 'supplier_id'}
                      direction={summarySort.key === 'supplier_id' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'supplier_id',
                          direction: prev.key === 'supplier_id' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Supplier Id
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'vault_other_net'}
                      direction={summarySort.key === 'vault_other_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'vault_other_net',
                          direction:
                            prev.key === 'vault_other_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Vault Other Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'vault_invoice_net'}
                      direction={summarySort.key === 'vault_invoice_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'vault_invoice_net',
                          direction:
                            prev.key === 'vault_invoice_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Vault Invoice Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'sap_invoice_net'}
                      direction={summarySort.key === 'sap_invoice_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'sap_invoice_net',
                          direction:
                            prev.key === 'sap_invoice_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      SAP Invoice Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'vault_credit_net'}
                      direction={summarySort.key === 'vault_credit_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'vault_credit_net',
                          direction:
                            prev.key === 'vault_credit_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Vault Credit Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'sap_credit_net'}
                      direction={summarySort.key === 'sap_credit_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'sap_credit_net',
                          direction: prev.key === 'sap_credit_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      SAP Credit Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'vault_overall_net'}
                      direction={summarySort.key === 'vault_overall_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'vault_overall_net',
                          direction:
                            prev.key === 'vault_overall_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Vault Overall Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'sap_overall_net'}
                      direction={summarySort.key === 'sap_overall_net' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'sap_overall_net',
                          direction:
                            prev.key === 'sap_overall_net' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      SAP Overall Amount (Net)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align="right">
                    <TableSortLabel
                      active={summarySort.key === 'accuracy_percent'}
                      direction={summarySort.key === 'accuracy_percent' ? summarySort.direction : 'asc'}
                      onClick={() =>
                        setSummarySort((prev) => ({
                          key: 'accuracy_percent',
                          direction:
                            prev.key === 'accuracy_percent' && prev.direction === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      Accuracy (%)
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row, index) => (
                  <TableRow
                    key={row.supplier_id}
                    sx={{
                      bgcolor:
                        row.supplier_id === detailSupplierId
                          ? '#d9e7f7'
                          : index % 2
                            ? '#fcfcfd'
                            : 'transparent',
                      '& td': {
                        bgcolor:
                          row.supplier_id === detailSupplierId
                            ? '#d9e7f7'
                            : index % 2
                              ? '#fcfcfd'
                              : 'transparent',
                      },
                      '&:hover td': {
                        bgcolor:
                          row.supplier_id === detailSupplierId
                            ? '#d9e7f7'
                            : index % 2
                              ? '#fcfcfd'
                              : 'transparent',
                      },
                      cursor: isUnspecifiedSupplier(row) ? 'default' : 'pointer',
                    }}
                    onClick={() => {
                      if (isUnspecifiedSupplier(row)) return;
                      openDetails(row.supplier_id, row.supplier_name);
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.supplier_name}</TableCell>
                    <TableCell>{row.supplier_id}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.vault_other_net)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.vault_invoice_net)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.sap_invoice_net)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.vault_credit_net)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.sap_credit_net)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.vault_overall_net)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.sap_overall_net)}
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.accuracy_percent || calculateAccuracy(row.vault_overall_net, row.sap_overall_net)).toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRows.length > 0 && (
                  <TableRow
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      bgcolor: '#ffffff',
                      boxShadow: '0 -2px 4px rgba(0,0,0,0.06)',
                      zIndex: 1,
                      '& td': {
                        bgcolor: '#ffffff',
                        fontWeight: 700,
                      },
                    }}
                  >
                    <TableCell />
                    <TableCell><BoldCellText>Total</BoldCellText></TableCell>
                    <TableCell />
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.vault_other_net)}</BoldCellText>
                    </TableCell>
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.vault_invoice_net)}</BoldCellText>
                    </TableCell>
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.sap_invoice_net)}</BoldCellText>
                    </TableCell>
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.vault_credit_net)}</BoldCellText>
                    </TableCell>
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.sap_credit_net)}</BoldCellText>
                    </TableCell>
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.vault_overall_net)}</BoldCellText>
                    </TableCell>
                    <TableCell align="right">
                      <BoldCellText align="right">{formatCurrency(filteredTotals.sap_overall_net)}</BoldCellText>
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#ffffff' }} align="right">
                      <BoldCellText align="right">
                        {calculateAccuracy(filteredTotals.vault_overall_net, filteredTotals.sap_overall_net).toFixed(2)}%
                      </BoldCellText>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {detailOpen && (
        <Paper ref={detailPanelRef} sx={{ p: { xs: 2, md: 3 }, mt: 2, position: 'relative', ...panelPaperSx }}>
          {detailLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.65)',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {detailTitle}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                size="small"
                variant={showMismatchesOnly ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => setShowMismatchesOnly((prev) => !prev)}
              >
                Mismatches Only
              </Button>
              <Box
                component="button"
                type="button"
                onClick={closeDetails}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Close
              </Box>
            </Box>
          </Box>
          {detailLoading && !detailHasLoadedOnce && <Box sx={{ height: 0 }} />}
          {detailError && (
            <Typography color="error" sx={{ mb: 2 }}>{detailError}</Typography>
          )}
          {!detailLoading && !detailError && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 360 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Vault Records
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <FormControl component="fieldset" sx={{ alignItems: 'center' }}>
                      <FormLabel sx={{ textAlign: 'center' }}>Missing In SAP</FormLabel>
                      <RadioGroup
                        row
                        value={vaultMissingFilter}
                        onChange={(event) => setVaultMissingFilter(event.target.value)}
                        disabled={showMismatchesOnly}
                      >
                        <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadVaultDetailsCsv}
                      disabled={detailLoading || !vaultDetailRows.length}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Download CSV
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {vaultFilterActive
                    ? `Filtered total: ${formatCurrency(vaultDetailTotalNet)} | Overall total: ${formatCurrency(vaultOverallTotalNet)}`
                    : `Overall total: ${formatCurrency(vaultOverallTotalNet)}`}
                </Typography>
                <TableContainer sx={{ maxHeight: '45vh', overflowX: 'auto', overflowY: 'auto' }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      minWidth: 600,
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        fontWeight: 700,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={vaultSort.key === 'invoice_number'}
                            direction={vaultSort.key === 'invoice_number' ? vaultSort.direction : 'asc'}
                            onClick={() =>
                              setVaultSort((prev) => ({
                                key: 'invoice_number',
                                direction:
                                  prev.key === 'invoice_number' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Document Number
                          </TableSortLabel>
                        </TableCell>
                        {(detailAllSuppliers || detailCombinedSuppliers) && (
                          <TableCell>
                            <TableSortLabel
                              active={vaultSort.key === 'supplier'}
                              direction={vaultSort.key === 'supplier' ? vaultSort.direction : 'asc'}
                              onClick={() =>
                                setVaultSort((prev) => ({
                                  key: 'supplier',
                                  direction: prev.key === 'supplier' && prev.direction === 'asc' ? 'desc' : 'asc',
                                }))
                              }
                            >
                              Supplier
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell>
                          <TableSortLabel
                            active={vaultSort.key === 'doc_type_id'}
                            direction={vaultSort.key === 'doc_type_id' ? vaultSort.direction : 'asc'}
                            onClick={() =>
                              setVaultSort((prev) => ({
                                key: 'doc_type_id',
                                direction:
                                  prev.key === 'doc_type_id' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Type
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={vaultSort.key === 'invoice_date'}
                            direction={vaultSort.key === 'invoice_date' ? vaultSort.direction : 'asc'}
                            onClick={() =>
                              setVaultSort((prev) => ({
                                key: 'invoice_date',
                                direction:
                                  prev.key === 'invoice_date' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={vaultSort.key === 'net'}
                            direction={vaultSort.key === 'net' ? vaultSort.direction : 'asc'}
                            onClick={() =>
                              setVaultSort((prev) => ({
                                key: 'net',
                                direction: prev.key === 'net' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Net
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={vaultSort.key === 'missing_in_sap'}
                            direction={vaultSort.key === 'missing_in_sap' ? vaultSort.direction : 'asc'}
                            onClick={() =>
                              setVaultSort((prev) => ({
                                key: 'missing_in_sap',
                                direction:
                                  prev.key === 'missing_in_sap' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Missing In SAP
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vaultDetailRows.map((record, index) => {
                          const missing = isMissingFlag(record.missing_in_sap);
                          const highlight = (!vaultMissingFilter || vaultMissingFilter === 'all') && missing;
                          return (
                            <TableRow
                              key={`sap-${index}`}
                              sx={{
                                backgroundColor: highlight
                                  ? missingHighlightColor
                                  : index % 2
                                    ? '#fcfcfd'
                                    : 'transparent',
                                '& td': {
                                  backgroundColor: highlight
                                    ? missingHighlightColor
                                    : index % 2
                                      ? '#fcfcfd'
                                      : 'transparent',
                                },
                              }}
                            >
                              <TableCell>{record.invoice_number}</TableCell>
                        {(detailAllSuppliers || detailCombinedSuppliers) && (
                                  <TableCell>{formatSupplierLabel(record)}</TableCell>
                                )}
                              <TableCell>{getDocTypeLabel(record.doc_type_id)}</TableCell>
                              <TableCell>{formatDate(record.invoice_date)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  backgroundColor:
                                    !missing &&
                                    (() => {
                                  const key = buildMatchKey(
                                        record.doc_type_id,
                                        record.invoice_number,
                                        record.supplier_id
                                      );
                                      if (!key) return false;
                                      const sapNets = sapNetsByKey.get(key);
                                      if (!sapNets || sapNets.length === 0) return false;
                                      const vaultNet = Number(record.net || 0);
                                      const allMatch = sapNets.every((net) => Number(net || 0) === vaultNet);
                                      return !allMatch;
                                    })()
                                      ? '#fff4cc !important'
                                      : 'inherit',
                                }}
                              >
                                {formatCurrency(record.net)}
                              </TableCell>
                              <TableCell align="center">{missing ? 'Yes' : 'No'}</TableCell>
                            </TableRow>
                          );
                      })}
                    </TableBody>
                    {vaultDetailRows.length > 0 && (
                      <TableFooter
                        sx={{
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          '& td': {
                            bgcolor: '#ffffff',
                            fontWeight: '700 !important',
                          },
                        }}
                      >
                        <TableRow sx={{ '& td': { fontWeight: '700 !important' } }}>
                          <TableCell>
                            <BoldCellText>Total</BoldCellText>
                          </TableCell>
                          {(detailAllSuppliers || detailCombinedSuppliers) && <TableCell />}
                          <TableCell />
                          <TableCell />
                          <TableCell align="right">
                            <BoldCellText align="right">{formatCurrency(vaultDetailTotalNet)}</BoldCellText>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </Box>
              <Box sx={{ flex: 1, minWidth: 360 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    SAP Records
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <FormControl component="fieldset" sx={{ alignItems: 'center' }}>
                      <FormLabel sx={{ textAlign: 'center' }}>Missing In Vault</FormLabel>
                      <RadioGroup
                        row
                        value={sapMissingFilter}
                        onChange={(event) => setSapMissingFilter(event.target.value)}
                        disabled={showMismatchesOnly}
                      >
                        <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                        <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadSapDetailsCsv}
                      disabled={detailLoading || !sapDetailRows.length}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Download CSV
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {sapFilterActive
                    ? `Filtered total: ${formatCurrency(sapDetailTotalNet)} | Overall total: ${formatCurrency(sapOverallTotalNet)}`
                    : `Overall total: ${formatCurrency(sapOverallTotalNet)}`}
                </Typography>
                <TableContainer sx={{ maxHeight: '45vh', overflowX: 'auto', overflowY: 'auto' }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      minWidth: 600,
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        fontWeight: 700,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sapSort.key === 'document_number'}
                            direction={sapSort.key === 'document_number' ? sapSort.direction : 'asc'}
                            onClick={() =>
                              setSapSort((prev) => ({
                                key: 'document_number',
                                direction:
                                  prev.key === 'document_number' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Document Number
                          </TableSortLabel>
                        </TableCell>
                        {(detailAllSuppliers || detailCombinedSuppliers) && (
                          <TableCell>
                            <TableSortLabel
                              active={sapSort.key === 'supplier'}
                              direction={sapSort.key === 'supplier' ? sapSort.direction : 'asc'}
                              onClick={() =>
                                setSapSort((prev) => ({
                                  key: 'supplier',
                                  direction: prev.key === 'supplier' && prev.direction === 'asc' ? 'desc' : 'asc',
                                }))
                              }
                            >
                              Supplier
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell>
                          <TableSortLabel
                            active={sapSort.key === 'document_type'}
                            direction={sapSort.key === 'document_type' ? sapSort.direction : 'asc'}
                            onClick={() =>
                              setSapSort((prev) => ({
                                key: 'document_type',
                                direction:
                                  prev.key === 'document_type' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Type
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sapSort.key === 'document_date'}
                            direction={sapSort.key === 'document_date' ? sapSort.direction : 'asc'}
                            onClick={() =>
                              setSapSort((prev) => ({
                                key: 'document_date',
                                direction:
                                  prev.key === 'document_date' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <TableSortLabel
                            active={sapSort.key === 'net'}
                            direction={sapSort.key === 'net' ? sapSort.direction : 'asc'}
                            onClick={() =>
                              setSapSort((prev) => ({
                                key: 'net',
                                direction: prev.key === 'net' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Net
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={sapSort.key === 'missing_in_vault'}
                            direction={sapSort.key === 'missing_in_vault' ? sapSort.direction : 'asc'}
                            onClick={() =>
                              setSapSort((prev) => ({
                                key: 'missing_in_vault',
                                direction:
                                  prev.key === 'missing_in_vault' && prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            Missing In Vault
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sapDetailRows.map((record, index) => {
                          const missing = isMissingFlag(record.missing_in_vault);
                          const highlight = (!sapMissingFilter || sapMissingFilter === 'all') && missing;
                          return (
                            <TableRow
                              key={`vault-${index}`}
                              sx={{
                                backgroundColor: highlight
                                  ? missingHighlightColor
                                  : index % 2
                                    ? '#fcfcfd'
                                    : 'transparent',
                                '& td': {
                                  backgroundColor: highlight
                                    ? missingHighlightColor
                                    : index % 2
                                      ? '#fcfcfd'
                                      : 'transparent',
                                },
                              }}
                            >
                              <TableCell>{record.document_number}</TableCell>
                        {(detailAllSuppliers || detailCombinedSuppliers) && (
                                  <TableCell>{formatSupplierLabel(record)}</TableCell>
                                )}
                              <TableCell>{getDocTypeLabel(record.document_type)}</TableCell>
                              <TableCell>{formatDate(record.document_date)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  backgroundColor:
                                    !missing &&
                                    (() => {
                                  const key = buildMatchKey(
                                        record.document_type,
                                        record.document_number,
                                        record.supplier_id
                                      );
                                      if (!key) return false;
                                      const vaultNets = vaultNetsByKey.get(key);
                                      if (!vaultNets || vaultNets.length === 0) return false;
                                      const sapNet = Number(record.net || 0);
                                      const allMatch = vaultNets.every((net) => Number(net || 0) === sapNet);
                                      return !allMatch;
                                    })()
                                      ? '#fff4cc !important'
                                      : 'inherit',
                                }}
                              >
                                {formatCurrency(record.net)}
                              </TableCell>
                              <TableCell align="center">{missing ? 'Yes' : 'No'}</TableCell>
                            </TableRow>
                          );
                      })}
                    </TableBody>
                    {sapDetailRows.length > 0 && (
                      <TableFooter
                        sx={{
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          '& td': {
                            bgcolor: '#ffffff',
                            fontWeight: '700 !important',
                          },
                        }}
                      >
                        <TableRow sx={{ '& td': { fontWeight: '700 !important' } }}>
                          <TableCell>
                            <BoldCellText>Total</BoldCellText>
                          </TableCell>
                          {(detailAllSuppliers || detailCombinedSuppliers) && <TableCell />}
                          <TableCell />
                          <TableCell />
                          <TableCell align="right">
                            <BoldCellText align="right">{formatCurrency(sapDetailTotalNet)}</BoldCellText>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SapBreakdownTab;
